import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  MapPin,
  Navigation,
  Search,
  CheckCircle2,
  Loader2,
  X,
  AlertCircle,
  ArrowRight,
  Building2,
} from "lucide-react";
import { toast } from "react-toastify";
import { isAuthenticated } from "../../utils/auth";

// Major Indian cities fallback dictionary for quick resolution
const POPULAR_CITIES = [
  { city: "New Delhi", state: "Delhi", pincode: "110001" },
  { city: "Mumbai", state: "Maharashtra", pincode: "400001" },
  { city: "Bengaluru", state: "Karnataka", pincode: "560001" },
  { city: "Kolkata", state: "West Bengal", pincode: "700001" },
  { city: "Chennai", state: "Tamil Nadu", pincode: "600001" },
  { city: "Hyderabad", state: "Telangana", pincode: "500001" },
  { city: "Amritsar", state: "Punjab", pincode: "143001" },
  { city: "Chandigarh", state: "Chandigarh", pincode: "160017" },
  { city: "Jaipur", state: "Rajasthan", pincode: "302001" },
  { city: "Ahmedabad", state: "Gujarat", pincode: "380001" },
  { city: "Pune", state: "Maharashtra", pincode: "411001" },
  { city: "Lucknow", state: "Uttar Pradesh", pincode: "226001" },
];

export default function DeliveryLocationModal({
  isOpen: externalIsOpen,
  onClose: externalOnClose,
}) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [isLocating, setIsLocating] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const searchTimeoutRef = useRef(null);

  // Check if mandatory: Logged in and no pincode saved yet
  const hasSavedPincode = Boolean(localStorage.getItem("user_pincode"));
  const isUserLoggedIn = isAuthenticated();
  const isMandatory = isUserLoggedIn && !hasSavedPincode;

  // Determine visibility: external control or mandatory auto-trigger
  const isVisible = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;

  // Check on mount and on storage events
  const checkMandatoryStatus = useCallback(() => {
    const pincode = localStorage.getItem("user_pincode");
    if (isAuthenticated() && !pincode) {
      setInternalIsOpen(true);
    } else if (externalIsOpen === undefined) {
      setInternalIsOpen(false);
    }
  }, [externalIsOpen]);

  useEffect(() => {
    checkMandatoryStatus();

    const handleAuthOrLocationChange = () => {
      checkMandatoryStatus();
    };

    window.addEventListener("delivery_location_updated", handleAuthOrLocationChange);
    window.addEventListener("storage", handleAuthOrLocationChange);

    return () => {
      window.removeEventListener("delivery_location_updated", handleAuthOrLocationChange);
      window.removeEventListener("storage", handleAuthOrLocationChange);
    };
  }, [checkMandatoryStatus]);

  // Load existing selection if available
  useEffect(() => {
    if (isVisible) {
      const savedLoc = localStorage.getItem("delivery_location");
      if (savedLoc) {
        try {
          setSelectedLocation(JSON.parse(savedLoc));
        } catch (e) {
          const p = localStorage.getItem("user_pincode");
          if (p) setSelectedLocation({ pincode: p });
        }
      }
    }
  }, [isVisible]);

  // Handle direct 6-digit Pincode verification via India Postal API
  const verifyPincode = async (pincode) => {
    setIsSearching(true);
    setErrorMsg("");
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await res.json();

      if (
        Array.isArray(data) &&
        data[0]?.Status === "Success" &&
        Array.isArray(data[0]?.PostOffice) &&
        data[0].PostOffice.length > 0
      ) {
        const poList = data[0].PostOffice;
        const mainPO = poList[0];
        const formattedResults = poList.slice(0, 8).map((po) => ({
          pincode: po.Pincode,
          area: po.Name,
          city: po.District || po.Division || mainPO.District,
          district: po.District,
          state: po.State,
          formattedAddress: `${po.Name}, ${po.District}, ${po.State} - ${po.Pincode}`,
        }));
        setResults(formattedResults);
        setSelectedLocation(formattedResults[0]);
      } else {
        // Fallback: If 6-digit number is valid regex
        if (/^[1-9][0-9]{5}$/.test(pincode)) {
          const fallbackLoc = {
            pincode,
            area: "India",
            city: `PIN ${pincode}`,
            state: "India",
            formattedAddress: `PIN Code: ${pincode}, India`,
          };
          setResults([fallbackLoc]);
          setSelectedLocation(fallbackLoc);
        } else {
          setErrorMsg("Invalid PIN code. Please enter a valid 6-digit Indian PIN code.");
          setResults([]);
        }
      }
    } catch (err) {
      // Fallback on network failure
      if (/^[1-9][0-9]{5}$/.test(pincode)) {
        const fallbackLoc = {
          pincode,
          area: "India",
          city: `PIN ${pincode}`,
          state: "India",
          formattedAddress: `PIN Code: ${pincode}, India`,
        };
        setResults([fallbackLoc]);
        setSelectedLocation(fallbackLoc);
      } else {
        setErrorMsg("Unable to verify PIN code right now. Please check your connection.");
      }
    } finally {
      setIsSearching(false);
    }
  };

  // Handle Location Name Search (City / Area / Suburb)
  const searchLocationByName = async (query) => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setErrorMsg("");

    const trimmed = query.trim();

    // Check if user entered a 6-digit number
    if (/^[1-9][0-9]{5}$/.test(trimmed)) {
      return verifyPincode(trimmed);
    }

    try {
      // Try India Postal API by post office/city name first
      let postalResults = [];
      try {
        const postalRes = await fetch(
          `https://api.postalpincode.in/postoffice/${encodeURIComponent(trimmed)}`
        );
        const postalData = await postalRes.json();
        if (
          Array.isArray(postalData) &&
          postalData[0]?.Status === "Success" &&
          Array.isArray(postalData[0]?.PostOffice)
        ) {
          postalResults = postalData[0].PostOffice.slice(0, 8).map((po) => ({
            pincode: po.Pincode,
            area: po.Name,
            city: po.District || po.Division,
            district: po.District,
            state: po.State,
            formattedAddress: `${po.Name}, ${po.District}, ${po.State} - ${po.Pincode}`,
          }));
        }
      } catch (e) {
        // Fallback to nominatim
      }

      // If India Post found matches, use them
      if (postalResults.length > 0) {
        setResults(postalResults);
        setSelectedLocation(postalResults[0]);
        setIsSearching(false);
        return;
      }

      // Otherwise try OpenStreetMap Geocoding
      const osmRes = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          trimmed
        )}&countrycodes=in&format=json&addressdetails=1&limit=6`
      );
      const osmData = await osmRes.json();

      if (Array.isArray(osmData) && osmData.length > 0) {
        const parsed = osmData
          .map((item) => {
            const addr = item.address || {};
            const pincode = addr.postcode || "";
            const city =
              addr.city ||
              addr.town ||
              addr.state_district ||
              addr.county ||
              addr.state ||
              trimmed;
            const area =
              addr.suburb ||
              addr.neighbourhood ||
              addr.road ||
              addr.residential ||
              item.display_name.split(",")[0];
            const state = addr.state || "";

            return {
              pincode: pincode ? pincode.replace(/\D/g, "").slice(0, 6) : null,
              area: area || city,
              city: city,
              district: addr.state_district || city,
              state: state,
              formattedAddress: item.display_name,
            };
          })
          .filter((item) => item.pincode && item.pincode.length === 6);

        if (parsed.length > 0) {
          setResults(parsed);
          setSelectedLocation(parsed[0]);
          setIsSearching(false);
          return;
        }
      }

      // Fallback to popular cities match
      const localMatch = POPULAR_CITIES.filter(
        (c) =>
          c.city.toLowerCase().includes(trimmed.toLowerCase()) ||
          c.state.toLowerCase().includes(trimmed.toLowerCase())
      );

      if (localMatch.length > 0) {
        const formatted = localMatch.map((c) => ({
          pincode: c.pincode,
          area: c.city,
          city: c.city,
          district: c.city,
          state: c.state,
          formattedAddress: `${c.city}, ${c.state} - ${c.pincode}`,
        }));
        setResults(formatted);
        setSelectedLocation(formatted[0]);
      } else {
        setErrorMsg(`No matching PIN code found for "${trimmed}". Try searching by city name or enter a 6-digit PIN code.`);
        setResults([]);
      }
    } catch (err) {
      setErrorMsg("Failed to search location. Please try entering a 6-digit PIN code.");
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced input change
  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    setErrorMsg("");

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!val || val.trim().length < 2) {
      setResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchLocationByName(val);
    }, 400);
  };

  // Use Current Location (GPS + Reverse Geocoding)
  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setIsLocating(true);
    setErrorMsg("");

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          // Reverse geocode using OpenStreetMap Nominatim
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`
          );
          const data = await res.json();

          const addr = data?.address || {};
          let pincode = addr.postcode ? addr.postcode.replace(/\D/g, "").slice(0, 6) : null;
          const city =
            addr.city ||
            addr.town ||
            addr.state_district ||
            addr.county ||
            addr.state ||
            "";
          const area =
            addr.suburb ||
            addr.neighbourhood ||
            addr.road ||
            addr.residential ||
            city;
          const state = addr.state || "";

          // If no postcode returned by Nominatim, try lookup via city
          if (!pincode && city) {
            const postalRes = await fetch(
              `https://api.postalpincode.in/postoffice/${encodeURIComponent(city)}`
            );
            const postalData = await postalRes.json();
            if (
              Array.isArray(postalData) &&
              postalData[0]?.Status === "Success" &&
              postalData[0]?.PostOffice?.[0]?.Pincode
            ) {
              pincode = postalData[0].PostOffice[0].Pincode;
            }
          }

          if (pincode && pincode.length === 6) {
            const loc = {
              pincode,
              area: area || city,
              city: city || "Current Location",
              district: addr.state_district || city,
              state: state,
              formattedAddress: data.display_name || `${area}, ${city}, ${state} - ${pincode}`,
            };
            setResults([loc]);
            setSelectedLocation(loc);
            setSearchInput(pincode);
            toast.success(`Location detected: ${loc.city} (${pincode})`);
          } else {
            setErrorMsg("Could not detect exact 6-digit PIN code. Please type your city or PIN code.");
          }
        } catch (err) {
          setErrorMsg("Failed to detect location address. Please enter PIN code manually.");
        } finally {
          setIsLocating(false);
        }
      },
      (err) => {
        setIsLocating(false);
        if (err.code === 1) {
          toast.error("Location permission denied. Please enter PIN code manually.");
        } else {
          toast.error("Could not retrieve GPS location.");
        }
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  // Save selected location to localStorage
  const handleSaveLocation = (locationToSave = selectedLocation) => {
    if (!locationToSave || !locationToSave.pincode) {
      toast.error("Please enter or select a valid delivery PIN code");
      return;
    }

    const cleanPincode = String(locationToSave.pincode).trim();
    if (!/^[1-9][0-9]{5}$/.test(cleanPincode)) {
      toast.error("Please enter a valid 6-digit Indian PIN code");
      return;
    }

    const payload = {
      pincode: cleanPincode,
      area: locationToSave.area || locationToSave.city || "Delivery Area",
      city: locationToSave.city || locationToSave.district || "India",
      district: locationToSave.district || locationToSave.city || "",
      state: locationToSave.state || "",
      formattedAddress:
        locationToSave.formattedAddress ||
        `${locationToSave.area || ""}, ${locationToSave.city || ""} - ${cleanPincode}`,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem("user_pincode", cleanPincode);
    localStorage.setItem("delivery_pincode", cleanPincode);
    localStorage.setItem("delivery_location", JSON.stringify(payload));

    window.dispatchEvent(new Event("delivery_location_updated"));
    toast.success(`Delivery location set to ${payload.city} (${cleanPincode})`);

    setInternalIsOpen(false);
    if (externalOnClose) externalOnClose();
  };

  const handleClose = () => {
    if (isMandatory) {
      toast.warning("Delivery location / PIN code is required to proceed.");
      return;
    }
    setInternalIsOpen(false);
    if (externalOnClose) externalOnClose();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-sm animate-fadeIn">
      <div
        className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl border border-gray-100 flex flex-col max-h-[90vh] transition-all transform animate-scaleUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-[#482607] to-[#63350a] p-5 text-white relative flex-shrink-0">
          {!isMandatory && (
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          )}

          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-md flex items-center justify-center text-[#77C712] shadow-inner">
              <MapPin size={24} className="stroke-[2.5]" />
            </div>
            <div>
              <h3 className="text-lg font-bold tracking-tight">
                Delivery Location
              </h3>
              <p className="text-xs text-amber-100/90 mt-0.5">
                {isMandatory
                  ? "Please provide your delivery PIN code to continue"
                  : "Check delivery speeds and product availability"}
              </p>
            </div>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto space-y-4 flex-1">
          {/* Mandatory Alert Banner */}
          {isMandatory && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2.5 text-xs text-amber-900">
              <AlertCircle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-bold">Required Delivery Location:</span>{" "}
                Enter your 6-digit PIN code or search your city/area to ensure accurate delivery and Ayurvedic consultation service.
              </div>
            </div>
          )}

          {/* Detect Location Button */}
          <button
            type="button"
            onClick={handleDetectLocation}
            disabled={isLocating}
            className="w-full flex items-center justify-center gap-2.5 py-3 px-4 rounded-xl border border-dashed border-[#77C712] bg-green-50/70 hover:bg-green-100/80 text-[#482607] text-xs sm:text-sm font-bold transition-all shadow-sm active:scale-[0.99] disabled:opacity-60"
          >
            {isLocating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-[#77C712]" />
                <span>Detecting your current location...</span>
              </>
            ) : (
              <>
                <Navigation className="w-4 h-4 text-[#77C712]" />
                <span>Use Current Location (GPS Auto-Detect)</span>
              </>
            )}
          </button>

          <div className="flex items-center gap-3 text-xs text-gray-400 my-2">
            <div className="flex-1 h-px bg-gray-200" />
            <span>OR ENTER LOCATION / PIN CODE</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Search Box */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-gray-700">
              Enter 6-digit PIN Code or City / Area Name:
            </label>
            <div className="relative flex items-center">
              <input
                type="text"
                placeholder="e.g. 143001, Amritsar, Delhi, Connaught Place"
                value={searchInput}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    searchLocationByName(searchInput);
                  }
                }}
                className="w-full h-11 pl-10 pr-10 bg-gray-50 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#77C712] focus:border-transparent text-gray-800"
              />
              <Search
                size={18}
                className="absolute left-3.5 text-gray-400 pointer-events-none"
              />
              {isSearching ? (
                <Loader2
                  size={18}
                  className="absolute right-3.5 text-[#77C712] animate-spin"
                />
              ) : searchInput ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput("");
                    setResults([]);
                    setErrorMsg("");
                  }}
                  className="absolute right-3 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              ) : null}
            </div>
          </div>

          {/* Error Message */}
          {errorMsg && (
            <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 text-red-500" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Results List */}
          {results.length > 0 && (
            <div className="space-y-2">
              <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                Select Your Area ({results.length}):
              </span>
              <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1 border border-gray-100 rounded-xl p-1 bg-gray-50/50">
                {results.map((loc, idx) => {
                  const isSelected =
                    selectedLocation?.pincode === loc.pincode &&
                    selectedLocation?.area === loc.area;

                  return (
                    <div
                      key={`${loc.pincode}-${idx}`}
                      onClick={() => {
                        setSelectedLocation(loc);
                      }}
                      className={`p-2.5 rounded-lg border transition-all cursor-pointer flex items-center justify-between gap-3 ${
                        isSelected
                          ? "bg-green-50 border-[#77C712] shadow-sm"
                          : "bg-white border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Building2
                          className={`w-4 h-4 flex-shrink-0 ${
                            isSelected ? "text-[#77C712]" : "text-gray-400"
                          }`}
                        />
                        <div className="truncate">
                          <p className="text-xs font-bold text-gray-800 truncate">
                            {loc.area || loc.city}
                          </p>
                          <p className="text-[11px] text-gray-500 truncate">
                            {loc.city ? `${loc.city}, ` : ""}
                            {loc.state || "India"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs font-extrabold px-2 py-0.5 rounded bg-gray-100 text-gray-800">
                          {loc.pincode}
                        </span>
                        {isSelected && (
                          <CheckCircle2 className="w-4 h-4 text-[#77C712]" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Popular Cities Quick Select */}
          {results.length === 0 && !isSearching && (
            <div className="space-y-2 pt-1">
              <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                Popular Cities:
              </span>
              <div className="flex flex-wrap gap-1.5">
                {POPULAR_CITIES.map((c) => (
                  <button
                    key={c.city}
                    type="button"
                    onClick={() => {
                      const loc = {
                        pincode: c.pincode,
                        area: c.city,
                        city: c.city,
                        district: c.city,
                        state: c.state,
                        formattedAddress: `${c.city}, ${c.state} - ${c.pincode}`,
                      };
                      setResults([loc]);
                      setSelectedLocation(loc);
                      setSearchInput(c.city);
                    }}
                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 hover:bg-green-100 text-gray-700 hover:text-green-800 transition-colors border border-gray-200"
                  >
                    {c.city} ({c.pincode})
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-3 flex-shrink-0">
          {selectedLocation ? (
            <div className="text-left leading-tight truncate max-w-[220px]">
              <span className="text-[10px] text-gray-500 font-medium block">
                Selected PIN:
              </span>
              <span className="text-xs font-bold text-gray-900 truncate block">
                {selectedLocation.area || selectedLocation.city} (
                {selectedLocation.pincode})
              </span>
            </div>
          ) : (
            <span className="text-xs text-gray-400 italic">
              No location selected
            </span>
          )}

          <div className="flex items-center gap-2">
            {!isMandatory && (
              <button
                type="button"
                onClick={handleClose}
                className="px-3 py-2 border border-gray-300 text-gray-700 rounded-xl text-xs font-semibold hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            )}

            <button
              type="button"
              onClick={() => handleSaveLocation()}
              disabled={!selectedLocation?.pincode}
              className="px-4 py-2 bg-[#77C712] hover:bg-green-600 disabled:opacity-50 text-white rounded-xl text-xs sm:text-sm font-bold transition-all shadow-md flex items-center gap-1.5 active:scale-95"
            >
              <span>Confirm & Save</span>
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
